import { useEffect, useState } from "react";
import api, { fileUrl, API } from "@/lib/api";
import { Card, gbp, Loading } from "@/components/ui-bits";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { toast } from "sonner";
import { ClipboardCheck, Plus, X, Loader2, Mail, Copy, ChevronRight, CheckCircle2, User, ArrowLeft, Send, ExternalLink, FileText, Download, FileSignature, ShieldCheck, AlertTriangle, Pencil } from "lucide-react";

const CHECKS = [
  { key: "credit", label: "Credit check" },
  { key: "affordability", label: "Affordability" },
  { key: "right_to_rent", label: "Right to Rent" },
  { key: "id", label: "ID verification" },
  { key: "previous_landlord", label: "Previous landlord" },
];
const CHECK_STATES = ["Pending", "Pass", "Refer", "Fail"];
const chkColor = (v) => v === "Pass" ? "bg-emerald-100 text-emerald-700 border-emerald-200"
  : v === "Refer" ? "bg-amber-100 text-amber-700 border-amber-200"
  : v === "Fail" ? "bg-red-100 text-red-700 border-red-200" : "bg-slate-100 text-slate-500 border-slate-200";
const statusColor = (s) => ({ Invited: "bg-slate-100 text-slate-600", "In Progress": "bg-blue-100 text-blue-700",
  Submitted: "bg-indigo-100 text-indigo-700", Approved: "bg-emerald-100 text-emerald-700",
  Referred: "bg-amber-100 text-amber-700", Declined: "bg-red-100 text-red-700" }[s] || "bg-slate-100 text-slate-600");

export function ReferencingPanel({ openCaseId }) {
  const [cases, setCases] = useState(null);
  const [sel, setSel] = useState(null);

  const load = async () => {
    const { data } = await api.get("/referencing");
    setCases(data);
    return data;
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!openCaseId) return;
    (async () => {
      const list = cases || (await load());
      const c = list.find((x) => x.case_id === openCaseId);
      if (c) { setSel(c); return; }
      try { const { data } = await api.get(`/referencing/${openCaseId}`); setSel(data); } catch { /* ignore */ }
    })();
  }, [openCaseId]); // eslint-disable-line

  const refreshSel = async () => {
    const { data } = await api.get(`/referencing/${sel.case_id}`);
    setSel(data);
    setCases((prev) => (prev || []).map((c) => c.case_id === data.case_id ? data : c));
  };

  if (sel) return <CaseDetailPanel case_={sel} onBack={() => { setSel(null); load(); }} refresh={refreshSel} />;

  return (
    <div>
      <div className="mb-4">
        <h2 className="font-display text-lg font-semibold text-slate-900">Referencing cases</h2>
        <p className="text-sm text-slate-500">Applications open automatically when a letting enters “Referencing”.</p>
      </div>
      {!cases ? <Loading /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="referencing-list">
          {cases.length === 0 && <Card className="p-10 text-center text-slate-400 text-sm md:col-span-2 lg:col-span-3">No referencing cases yet. Start one from a letting at the “Offers Received” stage.</Card>}
          {cases.map((c) => {
            const submitted = (c.applicants || []).filter((a) => a.status === "Submitted" || a.status === "Approved").length;
            return (
              <Card key={c.case_id} className="p-4 cursor-pointer hover:shadow-md transition-shadow fade-up" data-testid={`ref-case-${c.case_id}`} onClick={() => setSel(c)}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-mono text-slate-400">{c.reference}</p>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${c.status === "Complete" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>{c.status}</span>
                </div>
                <p className="text-sm font-semibold text-slate-900 truncate">{c.address || c.property_ref || "Property"}</p>
                <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><User size={12} /> {(c.applicants || []).length} applicant(s)</span>
                  <span>{submitted} submitted</span>
                </div>
                {c.holding_deposit > 0 && <p className="text-xs text-slate-400 mt-1">Holding deposit {gbp(c.holding_deposit)}</p>}
                <div className="mt-3 flex items-center justify-end text-blue-600 text-xs font-medium">Manage <ChevronRight size={13} /></div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CaseDetailPanel({ case_, onBack, refresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "Tenant", send_invite: true, current_address: "", less_than_3_years: false, previous_address: "" });
  const [busy, setBusy] = useState(false);
  const [links, setLinks] = useState({});
  const [filesByAppl, setFilesByAppl] = useState({});

  useEffect(() => {
    (async () => {
      const map = {};
      for (const a of case_.applicants || []) {
        try {
          const { data } = await api.get(`/files?entity_type=referencing_applicant&entity_id=${a.applicant_id}`);
          map[a.applicant_id] = data;
        } catch { map[a.applicant_id] = []; }
      }
      setFilesByAppl(map);
    })();
  }, [case_]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/referencing/${case_.case_id}/applicants`, form);
      setLinks((l) => ({ ...l, [data.applicant.applicant_id]: data.link }));
      if (form.send_invite) {
        if (data.email?.sent) toast.success(`Invite emailed to ${form.email}`);
        else toast.warning("Applicant added — email not delivered (test mode). Copy the link to share.");
      } else toast.success("Applicant added — copy the secure link to share.");
      setOpen(false); setForm({ name: "", email: "", phone: "", role: "Tenant", send_invite: true, current_address: "", less_than_3_years: false, previous_address: "" });
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add applicant"); }
    finally { setBusy(false); }
  };

  const setCheck = async (aid, key, value) => {
    try { await api.patch(`/referencing/${case_.case_id}/applicants/${aid}/checks`, { checks: { [key]: value } }); refresh(); }
    catch { toast.error("Could not update check"); }
  };
  const setStatus = async (aid, status) => {
    try { await api.patch(`/referencing/${case_.case_id}/applicants/${aid}/checks`, { status }); toast.success(`Marked ${status}`); refresh(); }
    catch { toast.error("Could not update status"); }
  };
  const saveCreditReport = async (aid, report) => {
    try { await api.patch(`/referencing/${case_.case_id}/applicants/${aid}/checks`, { credit_report: report }); toast.success("Credit report saved"); refresh(); }
    catch { toast.error("Could not save credit report"); }
  };
  const saveDetails = async (aid, details) => {
    try { await api.patch(`/referencing/${case_.case_id}/applicants/${aid}/details`, details); toast.success("Applicant details updated"); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update details"); }
  };
  const resend = async (aid) => {
    try { const { data } = await api.post(`/referencing/${case_.case_id}/applicants/${aid}/resend`, {}); setLinks((l) => ({ ...l, [aid]: data.link })); toast.success(data.email?.sent ? "Invite re-sent" : "Link ready to copy (email in test mode)"); }
    catch { toast.error("Could not resend"); }
  };
  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied");
      return;
    } catch { /* clipboard blocked (iframe/preview) — fall back */ }
    try {
      const ta = document.createElement("textarea");
      ta.value = link; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.focus(); ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) { toast.success("Link copied"); return; }
    } catch { /* ignore */ }
    window.prompt("Copy this secure link:", link);
  };

  return (
    <div>
      <button onClick={onBack} data-testid="ref-back-button" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft size={15} /> All referencing cases</button>
      <Card className="p-5 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs font-mono text-slate-400">{case_.reference}</p>
            <h2 className="font-display text-xl font-bold text-slate-900">{case_.address || case_.property_ref || "Property"}</h2>
            <p className="text-sm text-slate-600 mt-1">{case_.rent_pcm ? `${gbp(case_.rent_pcm)}/mo` : ""}{case_.holding_deposit > 0 ? ` · Holding deposit ${gbp(case_.holding_deposit)}` : ""}</p>
          </div>
          <button data-testid="add-applicant-button" onClick={() => setOpen(true)} className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 text-sm font-medium flex items-center gap-2"><Plus size={16} /> Add applicant</button>
        </div>
      </Card>

      <div className="space-y-4" data-testid="applicants-list">
        {(case_.applicants || []).length === 0 && <Card className="p-10 text-center text-slate-400 text-sm">No applicants yet. Add one and send them a secure referencing link.</Card>}
        {(case_.applicants || []).map((a) => (
          <Card key={a.applicant_id} className="p-5" data-testid={`applicant-${a.applicant_id}`}>
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-slate-900">{a.name}</p>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusColor(a.status)}`} data-testid={`applicant-status-${a.applicant_id}`}>{a.status}</span>
                  <span className="text-[10px] font-medium text-slate-400 border border-slate-200 rounded-full px-2 py-0.5">{a.role}</span>
                </div>
                <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5"><Mail size={12} /> {a.email}{a.phone ? <span className="text-slate-400">· {a.phone}</span> : null}</p>
                <p className="text-xs mt-1">Deposit: {a.full_deposit_status === "paid"
                  ? <span className="text-emerald-600 font-semibold">Full deposit paid</span>
                  : a.holding_deposit_status === "paid"
                    ? <span className="text-emerald-600 font-semibold">Holding deposit paid</span>
                    : <span className="text-slate-400">Unpaid</span>}</p>
              </div>
              <div className="flex items-center gap-2">
                <a href={`${window.location.origin}/apply/${a.token}`} target="_blank" rel="noreferrer" data-testid={`open-portal-${a.applicant_id}`}
                  className="border border-blue-200 text-blue-700 hover:bg-blue-50 rounded-lg px-3 py-2 text-xs font-medium flex items-center gap-1.5"><ExternalLink size={13} /> Open tenant portal</a>
                <button onClick={() => copyLink(`${window.location.origin}/apply/${a.token}`)} data-testid={`copy-link-${a.applicant_id}`} className="border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-lg px-3 py-2 text-xs font-medium flex items-center gap-1.5"><Copy size={13} /> Copy link</button>
                <button onClick={() => resend(a.applicant_id)} data-testid={`resend-${a.applicant_id}`} className="border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-lg px-3 py-2 text-xs font-medium flex items-center gap-1.5"><Send size={13} /> Email invite</button>
              </div>
            </div>

            <ApplicantEdit applicant={a} onSave={(d) => saveDetails(a.applicant_id, d)} />

            {a.form && Object.keys(a.form).length > 0 && (
              <details className="mb-4 rounded-lg border border-slate-100 bg-slate-50" data-testid={`answers-${a.applicant_id}`}>
                <summary className="cursor-pointer px-4 py-2.5 text-sm font-medium text-slate-700">Submitted answers {a.signature && <span className="text-xs text-emerald-600 ml-2">· signed by {a.signature}</span>}</summary>
                <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {Object.entries(a.form).filter(([, v]) => v !== "" && v != null).map(([k, v]) => (
                    <div key={k} className="flex flex-col"><span className="text-xs text-slate-400 capitalize">{k.replace(/_/g, " ")}</span><span className="text-slate-700">{String(v)}</span></div>
                  ))}
                </div>
              </details>
            )}

            {(filesByAppl[a.applicant_id] || []).length > 0 && (
              <div className="mb-4" data-testid={`applicant-docs-${a.applicant_id}`}>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Uploaded documents</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(filesByAppl[a.applicant_id] || []).map((f) => (
                    <a key={f.file_id} href={fileUrl(f.file_id)} target="_blank" rel="noreferrer" data-testid={`doc-${f.file_id}`}
                      className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 hover:bg-blue-50 hover:border-blue-200 transition-colors group">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText size={15} className="text-slate-400 shrink-0" />
                        <div className="min-w-0"><p className="text-sm text-slate-700 truncate">{f.original_filename}</p><p className="text-xs text-slate-400">{f.label}</p></div>
                      </div>
                      <Download size={15} className="text-slate-300 group-hover:text-blue-600 shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Reference checks</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {CHECKS.map((c) => (
                  <div key={c.key} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2">
                    <span className="text-sm text-slate-600">{c.label}</span>
                    <select data-testid={`check-${a.applicant_id}-${c.key}`} value={(a.checks || {})[c.key] || "Pending"} onChange={(e) => setCheck(a.applicant_id, c.key, e.target.value)}
                      className={`text-xs font-semibold rounded-full border px-2 py-1 focus:outline-none ${chkColor((a.checks || {})[c.key] || "Pending")}`}>
                      {CHECK_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {(a.open_banking_connected || (a.id_verification && a.id_verification.status)) && (
              <div className="mb-4 flex flex-wrap gap-2" data-testid={`auto-checks-${a.applicant_id}`}>
                {a.open_banking_connected && (
                  <span className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-800">
                    <ShieldCheck size={13} /> Open Banking · income {gbp(a.income_detected || (a.affordability_detail || {}).monthly_income || 0)}/mo
                  </span>
                )}
                {a.id_verification && a.id_verification.status && (
                  <span className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium ${a.id_verification.status === "verified" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
                    <ShieldCheck size={13} /> Stripe Identity · {a.id_verification.status.replace(/_/g, " ")}
                  </span>
                )}
              </div>
            )}

            <CreditReport applicant={a} onSave={(r) => saveCreditReport(a.applicant_id, r)} />

            <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
              <span className="text-xs font-semibold text-slate-500 mr-1">Decision:</span>
              <button onClick={() => setStatus(a.applicant_id, "Approved")} data-testid={`approve-${a.applicant_id}`} className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5"><CheckCircle2 size={13} /> Approve</button>
              <button onClick={() => setStatus(a.applicant_id, "Referred")} data-testid={`refer-${a.applicant_id}`} className="rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-50 px-3 py-1.5 text-xs font-semibold">Refer</button>
              <button onClick={() => setStatus(a.applicant_id, "Declined")} data-testid={`decline-${a.applicant_id}`} className="rounded-lg border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 text-xs font-semibold">Decline</button>
            </div>

            {a.status === "Approved" && <TenancyBlock caseId={case_.case_id} applicant={a} onReload={refresh} />}
          </Card>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-slate-900">Add applicant</h3>
              <button type="button" data-testid="close-applicant" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={add} className="space-y-4">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Full name</label>
                <input data-testid="applicant-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input data-testid="applicant-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Phone number</label>
                <input data-testid="applicant-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="e.g. 07123 456789" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <AddressAutocomplete label="Current address" testid="applicant-current-address" value={form.current_address}
                onChange={(v) => setForm({ ...form, current_address: v })}
                onSelect={(s) => setForm({ ...form, current_address: [s.address, s.city, s.postcode].filter(Boolean).join(", ") })} />
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" data-testid="applicant-lt3" checked={form.less_than_3_years} onChange={(e) => setForm({ ...form, less_than_3_years: e.target.checked })} /> Less than 3 years at this address
              </label>
              {form.less_than_3_years && (
                <AddressAutocomplete label="Previous address" testid="applicant-previous-address" placeholder="Previous address…" value={form.previous_address}
                  onChange={(v) => setForm({ ...form, previous_address: v })}
                  onSelect={(s) => setForm({ ...form, previous_address: [s.address, s.city, s.postcode].filter(Boolean).join(", ") })} />
              )}
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                <select data-testid="applicant-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                  <option>Tenant</option><option>Guarantor</option>
                </select></div>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" data-testid="send-invite" checked={form.send_invite} onChange={(e) => setForm({ ...form, send_invite: e.target.checked })} /> Email the secure invite now
              </label>
              <button type="submit" data-testid="save-applicant" disabled={busy} className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <ClipboardCheck size={16} />} Add & invite
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}


const agrStatusColor = (s) => ({ Draft: "bg-slate-100 text-slate-600", Sent: "bg-blue-100 text-blue-700",
  "Tenant Signed": "bg-indigo-100 text-indigo-700", Executed: "bg-emerald-100 text-emerald-700" }[s] || "bg-slate-100 text-slate-600");

function TenancyBlock({ caseId, applicant, onReload }) {
  const [agr, setAgr] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showGen, setShowGen] = useState(false);
  const [gen, setGen] = useState({ start_date: "" });
  const [countersig, setCountersig] = useState("");
  const [tenantUserId, setTenantUserId] = useState(null);
  const [elig, setElig] = useState(null);

  const confirmPay = async (kind) => {
    setBusy(true);
    try {
      await api.post(`/referencing/${caseId}/applicants/${applicant.applicant_id}/confirm-payment`, { kind, method: "Bank transfer / cash" });
      toast.success(kind === "rent" ? "First month's rent confirmed — tenancy is now live" : "Deposit balance confirmed received");
      if (onReload) await onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not confirm payment"); }
    finally { setBusy(false); }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/referencing/${caseId}/applicants/${applicant.applicant_id}/tenancy`);
      setAgr(data && data.agreement_id ? data : null);
    } catch { setAgr(null); }
    try {
      const { data } = await api.get(`/referencing/${caseId}/applicants/${applicant.applicant_id}/tenancy-eligibility`);
      setElig(data);
    } catch { setElig(null); }
    setLoaded(true);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/referencing/${caseId}/applicants/${applicant.applicant_id}/tenancy`, gen);
      setAgr(data); setShowGen(false); toast.success("Assured Tenancy agreement created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate"); }
    finally { setBusy(false); }
  };
  const sendToTenant = async () => {
    setBusy(true);
    try { await api.post(`/tenancy-agreements/${agr.agreement_id}/send`); toast.success("Sent to tenant for signing"); load(); }
    catch { toast.error("Could not send"); } finally { setBusy(false); }
  };
  const countersign = async () => {
    if (!countersig.trim()) { toast.error("Type your name to sign"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/tenancy-agreements/${agr.agreement_id}/countersign`, { signature: countersig.trim() });
      if (data.tenant_user_id) setTenantUserId(data.tenant_user_id);
      toast.success("Agreement executed 🎉 Tenant created & move-in scheduled");
      load();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not countersign"); } finally { setBusy(false); }
  };
  const sendLoginInvite = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenancy-agreements/${agr.agreement_id}/tenant-setup`, {});
      const creds = data.credentials || {};
      if (creds.emailed) {
        toast.success(`Login emailed to ${creds.email} · added to contacts & rental status`);
      } else if (creds.temp_password) {
        toast.success(`Login created for ${creds.email} (email in test mode) · copied`);
        try { await navigator.clipboard.writeText(`${creds.login_url}  ·  ${creds.email} / ${creds.temp_password}`); } catch { /* clipboard blocked */ }
      } else {
        toast.success(`Tenant linked (${creds.email}) · added to contacts & rental status`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set up the tenant login");
    } finally { setBusy(false); }
  };
  const downloadPdf = async () => {
    try {
      const res = await api.get(`/tenancy-agreements/${agr.agreement_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); window.open(url, "_blank");
    } catch { toast.error("Could not open PDF"); }
  };

  if (!loaded) return null;

  return (
    <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`tenancy-block-${applicant.applicant_id}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5"><FileSignature size={13} /> Assured Tenancy agreement</p>
        {agr && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${agrStatusColor(agr.status)}`} data-testid={`tenancy-status-${applicant.applicant_id}`}>{agr.status}</span>}
      </div>

      {!agr ? (
        <div>
          {elig && !elig.eligible && (
            <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid={`tenancy-compliance-warning-${applicant.applicant_id}`}>
              <p className="text-xs font-semibold text-amber-900 flex items-center gap-1.5"><AlertTriangle size={13} /> Property not ready to let — in-date certificates required</p>
              <ul className="mt-1.5 space-y-0.5">
                {elig.problems.map((p, i) => <li key={i} className="text-xs text-amber-800 flex items-start gap-1.5"><span className="mt-1 w-1 h-1 rounded-full bg-amber-500 shrink-0" />{p}</li>)}
              </ul>
              <p className="text-[11px] text-amber-700 mt-1.5">Upload in-date EPC, Gas Safety and EICR certificates (with expiry dates) on the property's Documents tab, then refresh.</p>
            </div>
          )}
          <button data-testid={`generate-tenancy-${applicant.applicant_id}`} onClick={() => setShowGen(true)} disabled={elig && !elig.eligible}
            className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"><FileSignature size={14} /> Generate tenancy agreement</button>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
          <p className="text-xs text-slate-500 mb-2">{agr.agreement_type} · deposit {gbp(agr.deposit)} · start {agr.start_date || "—"} · {(agr.compliance_docs || []).length} compliance docs</p>
          {agr.tenant_signature && <p className="text-xs text-slate-600">Tenant signed: <b>{agr.tenant_signature}</b></p>}
          {agr.agent_signature && <p className="text-xs text-slate-600">Countersigned: <b>{agr.agent_signature}</b></p>}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <button onClick={downloadPdf} data-testid={`tenancy-pdf-${applicant.applicant_id}`} className="border border-slate-300 text-slate-600 hover:bg-white rounded-lg px-3 py-1.5 text-xs font-medium flex items-center gap-1.5"><Download size={13} /> View PDF</button>
            {agr.status === "Draft" && <button onClick={sendToTenant} disabled={busy} data-testid={`send-tenancy-${applicant.applicant_id}`} className="bg-blue-700 hover:bg-blue-800 text-white rounded-lg px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-60"><Send size={13} /> Send to tenant</button>}
            {agr.status === "Sent" && <span className="text-xs text-blue-700 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Awaiting tenant signature…</span>}
            {agr.status === "Tenant Signed" && (
              <div className="flex items-center gap-2">
                <input data-testid={`countersign-input-${applicant.applicant_id}`} value={countersig} onChange={(e) => setCountersig(e.target.value)} placeholder="Type your name" className="rounded-md border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-600" />
                <button onClick={countersign} disabled={busy} data-testid={`countersign-${applicant.applicant_id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-60"><CheckCircle2 size={13} /> Countersign & execute</button>
              </div>
            )}
            {agr.status === "Executed" && <span className="text-xs text-emerald-700 font-semibold flex items-center gap-1"><CheckCircle2 size={13} /> Fully executed</span>}
            {agr.status === "Executed" && <button onClick={sendLoginInvite} disabled={busy} data-testid={`tenant-invite-${applicant.applicant_id}`} className="border border-slate-300 text-slate-600 hover:bg-white rounded-lg px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 disabled:opacity-60"><Send size={13} /> Tenant login invite</button>}
          </div>
          {agr.status === "Executed" && (
            <div className="mt-3 pt-3 border-t border-slate-200 space-y-2" data-testid={`payments-${applicant.applicant_id}`}>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Move-in payments</p>
              <PayRow label="Deposit balance" done={applicant.full_deposit_status === "paid"} busy={busy}
                onConfirm={() => confirmPay("deposit")} testid={`confirm-deposit-${applicant.applicant_id}`} />
              <PayRow label="First month's rent" done={applicant.rent_status === "paid"} busy={busy}
                onConfirm={() => confirmPay("rent")} testid={`confirm-rent-${applicant.applicant_id}`} />
              {applicant.rent_status === "paid" && <p className="text-xs text-emerald-700 font-medium flex items-center gap-1"><CheckCircle2 size={13} /> Tenancy is live — property marked Tenanted</p>}
            </div>
          )}
        </div>
      )}

      {showGen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setShowGen(false)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-bold text-slate-900">Generate Assured Tenancy</h3>
              <button onClick={() => setShowGen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">Creates a periodic Assured Tenancy (Renters' Rights Act 2025) for <b>{applicant.name}</b>. Compliance certificates (Gas, EICR, EPC) and the How to Rent guide are attached automatically for the tenant to review before signing.</p>
            <label className="block text-sm font-medium text-slate-700 mb-1">Tenancy start date</label>
            <input type="date" data-testid={`gen-start-${applicant.applicant_id}`} value={gen.start_date} onChange={(e) => setGen({ ...gen, start_date: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <button onClick={generate} disabled={busy} data-testid={`confirm-generate-${applicant.applicant_id}`} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-4 py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <FileSignature size={16} />} Create agreement
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function PayRow({ label, done, busy, onConfirm, testid }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-slate-600">{label}</span>
      {done ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-0.5 text-[11px] font-semibold"><CheckCircle2 size={12} /> Paid</span>
      ) : (
        <button onClick={onConfirm} disabled={busy} data-testid={testid}
          className="border border-slate-300 text-slate-600 hover:bg-white rounded-md px-2.5 py-1 text-[11px] font-medium disabled:opacity-60">
          Mark received (bank/cash)
        </button>
      )}
    </div>
  );
}


function CreditReport({ applicant, onSave }) {
  const cr = applicant.credit_report || {};
  const [open, setOpen] = useState(false);
  const toCcjs = (c) => Array.isArray(c.ccjs) && c.ccjs.length
    ? c.ccjs.map((x) => ({ amount: x.amount ?? "", creditor: x.creditor || "", date: x.date || "", satisfied: !!x.satisfied }))
    : (c.has_ccj ? [{ amount: c.ccj_amount ?? "", creditor: c.ccj_creditor || "", date: c.ccj_date || "", satisfied: !!c.ccj_satisfied }] : []);
  const [f, setF] = useState({
    provider: cr.provider || "Experian", score: cr.score || "",
    has_ccj: applicant.credit_report ? !!cr.has_ccj : null, ccjs: toCcjs(cr), notes: cr.notes || "",
  });
  useEffect(() => {
    const c = applicant.credit_report || {};
    const ccjs = Array.isArray(c.ccjs) && c.ccjs.length
      ? c.ccjs.map((x) => ({ amount: x.amount ?? "", creditor: x.creditor || "", date: x.date || "", satisfied: !!x.satisfied }))
      : (c.has_ccj ? [{ amount: c.ccj_amount ?? "", creditor: c.ccj_creditor || "", date: c.ccj_date || "", satisfied: !!c.ccj_satisfied }] : []);
    setF({ provider: c.provider || "Experian", score: c.score || "", has_ccj: applicant.credit_report ? !!c.has_ccj : null, ccjs, notes: c.notes || "" });
  }, [applicant.credit_report]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setCcj = (i, k, v) => setF((p) => ({ ...p, ccjs: p.ccjs.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const addCcj = () => setF((p) => ({ ...p, ccjs: [...p.ccjs, { amount: "", creditor: "", date: "", satisfied: false }] }));
  const removeCcj = (i) => setF((p) => ({ ...p, ccjs: p.ccjs.filter((_, idx) => idx !== i) }));
  const saved = applicant.credit_report;

  const ccjName = (applicant.form?.full_name || applicant.name || "").trim();
  const ccjAddress = (applicant.form?.current_address || "").trim();

  const copyText = async (text, label) => {
    if (!text) { toast.error(`No ${label.toLowerCase()} on file`); return; }
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied — paste into the search box`); }
    catch { toast.error("Couldn't copy — select the text and copy manually"); }
  };

  const runCcjCheck = async () => {
    if (ccjName) { try { await navigator.clipboard.writeText(ccjName); } catch { /* clipboard blocked */ } }
    window.open("https://search.trustonline.org.uk/Search/Person", "_blank", "noopener");
    toast.success("TrustOnline opened — choose the £6 “England & Wales — Single” option; the name is copied, ready to paste");
  };

  return (
    <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/60" data-testid={`credit-report-${applicant.applicant_id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-3 py-2.5">
        <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5"><FileText size={13} /> Credit &amp; CCJ check {saved ? (saved.has_ccj ? (() => {
          const list = Array.isArray(saved.ccjs) && saved.ccjs.length ? saved.ccjs : (saved.ccj_amount ? [{ amount: saved.ccj_amount, satisfied: saved.ccj_satisfied }] : []);
          const total = list.reduce((a, c) => a + (Number(c.amount) || 0), 0);
          const n = list.length;
          const allSat = n > 0 && list.every((c) => c.satisfied);
          return <span className="ml-1 text-red-600 normal-case font-medium">· {n} CCJ{n === 1 ? "" : "s"}: {gbp(total)}{allSat ? " (all satisfied)" : ""}</span>;
        })() : <span className="ml-1 text-emerald-600 normal-case font-medium">· No CCJs</span>) : <span className="ml-1 text-slate-400 normal-case font-normal">· not recorded</span>}</span>
        <ChevronRight size={14} className={`text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2.5">
            <div>
              <p className="text-xs font-semibold text-blue-900">Run a CCJ check on the official register</p>
              <p className="text-xs text-blue-800 mt-0.5">Opens the official Registry Trust register (TrustOnline). Choose the <b>£6 “England &amp; Wales — Single”</b> option, paste the applicant's name (copied automatically) and enter the address, then record the result below. (Bureaus like Experian need a commercial contract, so CCJs are recorded manually.)</p>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0 rounded-md border border-blue-200 bg-white px-2 py-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 block">Name</span>
                  <span className="text-sm text-slate-800 truncate block" data-testid={`ccj-name-${applicant.applicant_id}`}>{ccjName || <span className="text-slate-400">— not provided —</span>}</span>
                </div>
                <button type="button" onClick={() => copyText(ccjName, "Name")} title="Copy name"
                  className="shrink-0 inline-flex items-center gap-1 rounded-md border border-blue-200 bg-white hover:bg-blue-100 text-blue-800 px-2 py-1.5 text-xs font-medium"><Copy size={12} /> Copy</button>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0 rounded-md border border-blue-200 bg-white px-2 py-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 block">Current address</span>
                  <span className="text-sm text-slate-800 truncate block" data-testid={`ccj-address-${applicant.applicant_id}`}>{ccjAddress || <span className="text-slate-400">— not provided —</span>}</span>
                </div>
                <button type="button" onClick={() => copyText(ccjAddress, "Address")} title="Copy address"
                  className="shrink-0 inline-flex items-center gap-1 rounded-md border border-blue-200 bg-white hover:bg-blue-100 text-blue-800 px-2 py-1.5 text-xs font-medium"><Copy size={12} /> Copy</button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-0.5">
              <button type="button" onClick={runCcjCheck} data-testid={`trustonline-${applicant.applicant_id}`}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-700 hover:bg-blue-800 text-white px-3 py-1.5 text-xs font-semibold"><ExternalLink size={13} /> Run CCJ check — TrustOnline (£6)</button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-slate-500">Provider / source
              <input data-testid={`cr-provider-${applicant.applicant_id}`} value={f.provider} onChange={(e) => set("provider", e.target.value)} className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></label>
            <label className="text-xs text-slate-500">Credit score
              <input data-testid={`cr-score-${applicant.applicant_id}`} value={f.score} onChange={(e) => set("score", e.target.value)} placeholder="e.g. 780" className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></label>
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1.5">Are there any County Court Judgments (CCJs)?</p>
            <div className="flex gap-2" data-testid={`cr-hasccj-${applicant.applicant_id}`}>
              <button type="button" data-testid={`cr-hasccj-yes-${applicant.applicant_id}`}
                onClick={() => setF((p) => ({ ...p, has_ccj: true, ccjs: p.ccjs.length === 0 ? [{ amount: "", creditor: "", date: "", satisfied: false }] : p.ccjs }))}
                className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold border transition-colors ${f.has_ccj === true ? "bg-red-600 border-red-600 text-white" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}>Yes</button>
              <button type="button" data-testid={`cr-hasccj-no-${applicant.applicant_id}`}
                onClick={() => setF((p) => ({ ...p, has_ccj: false, ccjs: [] }))}
                className={`flex-1 rounded-lg px-3 py-2 text-sm font-semibold border transition-colors ${f.has_ccj === false ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}>No</button>
            </div>
          </div>
          {f.has_ccj && (
            <div className="space-y-3">
              {f.ccjs.map((c, i) => (
                <div key={i} className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-3" data-testid={`ccj-entry-${applicant.applicant_id}-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-red-700 uppercase tracking-wider">CCJ #{i + 1}</span>
                    {f.ccjs.length > 1 && (
                      <button type="button" onClick={() => removeCcj(i)} data-testid={`ccj-remove-${applicant.applicant_id}-${i}`} className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-700"><X size={12} /> Remove</button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="text-xs text-slate-600">CCJ amount (£)
                      <input type="number" data-testid={`cr-amount-${applicant.applicant_id}-${i}`} value={c.amount} onChange={(e) => setCcj(i, "amount", e.target.value)} className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" /></label>
                    <label className="text-xs text-slate-600">Judgment date
                      <input type="date" data-testid={`cr-date-${applicant.applicant_id}-${i}`} value={c.date} onChange={(e) => setCcj(i, "date", e.target.value)} className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" /></label>
                  </div>
                  <label className="text-xs text-slate-600 block">Owed to (creditor)
                    <input data-testid={`cr-creditor-${applicant.applicant_id}-${i}`} value={c.creditor} onChange={(e) => setCcj(i, "creditor", e.target.value)} placeholder="e.g. Lowell Financial" className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" /></label>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" data-testid={`cr-satisfied-${applicant.applicant_id}-${i}`} checked={c.satisfied} onChange={(e) => setCcj(i, "satisfied", e.target.checked)} className="accent-emerald-600" />
                    Marked as satisfied (paid)
                  </label>
                </div>
              ))}
              <button type="button" onClick={addCcj} data-testid={`ccj-add-${applicant.applicant_id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-white hover:bg-red-50 text-red-700 px-3 py-1.5 text-xs font-semibold"><Plus size={13} /> Add another CCJ</button>
            </div>
          )}
          <label className="text-xs text-slate-500 block">Notes
            <textarea data-testid={`cr-notes-${applicant.applicant_id}`} rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Defaults, arrears, other findings from the report…" className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></label>
          <button disabled={f.has_ccj === null} onClick={() => onSave({ provider: f.provider, score: f.score, has_ccj: f.has_ccj, ccjs: f.has_ccj ? f.ccjs.map((c) => ({ amount: parseFloat(c.amount) || 0, creditor: c.creditor, date: c.date, satisfied: c.satisfied })) : [], notes: f.notes })}
            data-testid={`cr-save-${applicant.applicant_id}`} className="bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg px-3 py-1.5 text-xs font-semibold" title={f.has_ccj === null ? "Answer the CCJ question first" : ""}>Save credit report</button>
        </div>
      )}
    </div>
  );
}

function ApplicantEdit({ applicant, onSave }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const build = () => {
    const g = applicant.form || {};
    return {
      name: applicant.name || "", email: applicant.email || "", phone: applicant.phone || "", role: applicant.role || "Tenant",
      current_address: g.current_address || "",
      less_than_3_years: !!g.less_than_3_years || !!g.previous_address,
      previous_address: g.previous_address || "",
    };
  };
  const [d, setD] = useState(build);
  useEffect(() => { setD(build()); /* eslint-disable-next-line */ }, [applicant]);
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    await onSave({ ...d, previous_address: d.less_than_3_years ? d.previous_address : "" });
    setBusy(false); setOpen(false);
  };
  const addr = (applicant.form || {}).current_address;
  return (
    <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/60" data-testid={`applicant-edit-${applicant.applicant_id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-3 py-2.5">
        <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5"><Pencil size={13} /> Applicant details {addr ? <span className="ml-1 text-slate-500 normal-case font-normal">· {addr}</span> : <span className="ml-1 text-slate-400 normal-case font-normal">· add current address</span>}</span>
        <ChevronRight size={14} className={`text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-slate-500">Full name
              <input data-testid={`ae-name-${applicant.applicant_id}`} value={d.name} onChange={(e) => set("name", e.target.value)} className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></label>
            <label className="text-xs text-slate-500">Phone
              <input data-testid={`ae-phone-${applicant.applicant_id}`} value={d.phone} onChange={(e) => set("phone", e.target.value)} className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></label>
          </div>
          <label className="text-xs text-slate-500 block">Email
            <input data-testid={`ae-email-${applicant.applicant_id}`} type="email" value={d.email} onChange={(e) => set("email", e.target.value)} className="mt-0.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></label>
          <AddressAutocomplete label="Current address" testid={`ae-address-${applicant.applicant_id}`} value={d.current_address}
            onChange={(v) => set("current_address", v)}
            onSelect={(s) => set("current_address", [s.address, s.city, s.postcode].filter(Boolean).join(", "))} />
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" data-testid={`ae-lt3-${applicant.applicant_id}`} checked={d.less_than_3_years} onChange={(e) => set("less_than_3_years", e.target.checked)} />
            Less than 3 years at this address
          </label>
          {d.less_than_3_years && (
            <AddressAutocomplete label="Previous address" testid={`ae-prev-${applicant.applicant_id}`} placeholder="Previous address…" value={d.previous_address}
              onChange={(v) => set("previous_address", v)}
              onSelect={(s) => set("previous_address", [s.address, s.city, s.postcode].filter(Boolean).join(", "))} />
          )}
          <button onClick={save} disabled={busy} data-testid={`ae-save-${applicant.applicant_id}`} className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 disabled:opacity-60">{busy ? <Loader2 size={13} className="animate-spin" /> : null} Save details</button>
        </div>
      )}
    </div>
  );
}

